import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Spinner from 'react-bootstrap/Spinner';
import Tooltip from 'react-bootstrap/Tooltip';
import { Panel } from '@xyflow/react';
import Icon from './Icon';
import type { IconName } from './Icon';

type ExportType =
  | 'preview-png'
  | 'png'
  | 'jpeg'
  | 'svg'
  | 'json'
  | 'csv'
  | 'txt'
  | 'copy-keywords'
  | 'copy-path';

type TipButtonProps = {
  id: string;
  title: string;
  icon: IconName;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
};

type ExportToggleProps = ComponentPropsWithoutRef<typeof Button> & {
  exporting: boolean;
};

type ExportItemProps = {
  icon: IconName;
  title: string;
  onClick: () => void;
  disabled?: boolean;
};

type ClusterCanvasControlsProps = {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  fullscreen?: boolean;
  onToggleFullscreen: () => void;
  exporting?: boolean;
  onExport?: (type: ExportType) => void;
  hasSelection?: boolean;
};

const btnClass =
  'bg-100 d-flex align-items-center justify-content-center p-2 text-700';

function TipButton({
  id,
  title,
  icon,
  onClick,
  className = '',
  disabled = false
}: TipButtonProps) {
  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip id={`canvas-tip-${id}`}>{title}</Tooltip>}
    >
      <Button
        type="button"
        variant="link"
        size="sm"
        className={`${btnClass} ${className}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
      >
        <Icon name={icon} className="fs--2" />
      </Button>
    </OverlayTrigger>
  );
}

const ExportToggle = forwardRef<HTMLButtonElement, ExportToggleProps>(function ExportToggle(
  { onClick, disabled, exporting, className: _className, ...rest },
  ref
) {
  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip id="canvas-tip-export">خروجی</Tooltip>}
    >
      <Button
        ref={ref}
        type="button"
        variant="link"
        size="sm"
        className={`${btnClass} rounded-3 border border-400 w-100`}
        onClick={onClick}
        disabled={disabled}
        aria-label="خروجی"
        {...rest}
      >
        {exporting ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Icon name="download" className="fs--2" />
        )}
      </Button>
    </OverlayTrigger>
  );
});

function ExportItem({ icon, title, onClick, disabled = false }: ExportItemProps) {
  return (
    <Dropdown.Item
      className="d-flex align-items-center gap-2 py-1 px-2 fs--1"
      onClick={onClick}
      disabled={disabled}
    >
      <Icon name={icon} className="text-primary fs--2" />
      <span>{title}</span>
    </Dropdown.Item>
  );
}

/** Floating chart controls — same visual language as Tree (نمایش مرکزی). */
export default function ClusterCanvasControls({
  onZoomIn,
  onZoomOut,
  onFitView,
  fullscreen = false,
  onToggleFullscreen,
  exporting = false,
  onExport,
  hasSelection = false
}: ClusterCanvasControlsProps) {
  return (
    <Panel
      position="top-left"
      className="mt-2 ms-3"
    >
      <div className="d-flex flex-column" style={{ direction: 'ltr' }}>
        <div className="rounded-3 bg-body-tertiary border border-400 overflow-hidden">
          <TipButton
            id="zoom-in"
            title="بزرگ‌نمایی"
            icon="plus-lg"
            className="rounded-bottom-0 w-100"
            onClick={onZoomIn}
          />
          <hr className="m-0 text-300" />
          <TipButton
            id="fit"
            title="بازنشانی زوم"
            icon="arrow-counterclockwise"
            className="rounded-0 w-100"
            onClick={onFitView}
          />
          <hr className="m-0 text-300" />
          <TipButton
            id="zoom-out"
            title="کوچک‌نمایی"
            icon="dash-lg"
            className="rounded-top-0 w-100"
            onClick={onZoomOut}
          />
        </div>

        <div className="bg-body-tertiary mt-3">
          <TipButton
            id="fullscreen"
            title="تمام‌صفحه"
            icon={fullscreen ? 'fullscreen-exit' : 'fullscreen'}
            className="rounded-3 border border-400 w-100"
            onClick={onToggleFullscreen}
          />
        </div>

        <div className="bg-body-tertiary mt-1">
          <Dropdown align="start">
            <Dropdown.Toggle
              as={ExportToggle}
              disabled={exporting}
              exporting={exporting}
              id="cluster-canvas-export"
            />
            <Dropdown.Menu
              className="shadow border-0 py-1"
              style={{ minWidth: 190 }}
              dir="rtl"
            >
              <ExportItem
                icon="eye"
                title="خروجی پیش‌نمایش PNG"
                onClick={() => onExport?.('preview-png')}
              />
              <ExportItem
                icon="filetype-png"
                title="خروجی PNG"
                onClick={() => onExport?.('png')}
              />
              <ExportItem
                icon="filetype-jpg"
                title="خروجی JPEG"
                onClick={() => onExport?.('jpeg')}
              />
              <ExportItem
                icon="filetype-svg"
                title="خروجی SVG"
                onClick={() => onExport?.('svg')}
              />
              <ExportItem
                icon="filetype-json"
                title="خروجی JSON"
                onClick={() => onExport?.('json')}
              />
              <ExportItem
                icon="filetype-csv"
                title="خروجی CSV"
                onClick={() => onExport?.('csv')}
              />
              <ExportItem
                icon="file-text"
                title="خروجی TXT"
                onClick={() => onExport?.('txt')}
              />
              <Dropdown.Divider className="my-1" />
              <ExportItem
                icon="copy"
                title="کپی همه کلمات"
                onClick={() => onExport?.('copy-keywords')}
              />
              <ExportItem
                icon="signpost-split"
                title="کپی مسیر انتخاب‌شده"
                onClick={() => onExport?.('copy-path')}
                disabled={!hasSelection}
              />
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </Panel>
  );
}
